import {
  Link,
  Outlet,
  useLocation,
  useRouter,
  useNavigate,
} from "@tanstack/react-router";
import {
  Home,
  ShoppingCart,
  Package,
  BookOpen,
  Receipt,
  LogOut,
  User,
  Settings,
  Users,
  UserCircle,
  ReceiptText,
  FileText,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { authStore, hasRole, type AuthUser, useAuth } from "@/lib/auth-store";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

type TabDef = {
  to:
    | "/"
    | "/sell"
    | "/tabs-admin"
    | "/tabs-waiter"
    | "/inventory"
    | "/cashbook"
    | "/reports"
    | "/tab";
  label: string;
  icon: typeof Home;
  minRole?: "viewer" | "waiter" | "cashier" | "manager" | "admin";
};

const tabs: TabDef[] = [
  { to: "/", label: "Home", icon: Home, minRole: "cashier" },
  { to: "/sell", label: "Menu", icon: ShoppingCart, minRole: "viewer" },
  {
    to: "/tabs-waiter",
    label: "Customers",
    icon: ReceiptText,
    minRole: "waiter",
  },
  { to: "/tabs-admin", label: "Bills", icon: ReceiptText, minRole: "cashier" },
  { to: "/inventory", label: "Stock", icon: Package, minRole: "manager" },
  { to: "/cashbook", label: "Cash", icon: BookOpen, minRole: "manager" },
  { to: "/reports", label: "Reports", icon: Receipt, minRole: "cashier" },
  { to: "/tab", label: "My Tab", icon: UserCircle, minRole: "viewer" },
];

export function AppShell({ user }: { user: AuthUser | null }) {
  const loc = useLocation();
  const router = useRouter();
  const isViewer = user?.role === "viewer";
  const visible = user
    ? tabs.filter((t) => {
        // Viewers only see Menu and My Tab
        if (isViewer) return t.to === "/sell" || t.to === "/tab";
        // Waiters see only their customer page
        if (user.role === "waiter") return t.to === "/tabs-waiter";
        // Staff never see the "My Tab" page
        if (t.to === "/tab" || t.to === "/tabs-waiter") return false;
        return hasRole(user, t.minRole ?? "viewer");
      })
    : [];

  const handleLogout = async () => {
    await authStore.logout();
    router.invalidate();
  };

  return (
    <div className="mx-auto flex min-h-screen max-w-md flex-col bg-background">
      <main className="flex-1 pb-24">
        <Outlet />
      </main>

      {/* Bottom navigation */}
      {user && (
        <nav className="fixed inset-x-0 bottom-0 z-40 mx-auto max-w-md border-t border-border bg-card/95 backdrop-blur safe-area-bottom">
          <ul className="flex overflow-x-auto">
            {visible.map((t) => {
              const active =
                loc.pathname === t.to ||
                (t.to !== "/" && loc.pathname.startsWith(t.to));
              const Icon = t.icon;
              return (
                <li key={t.to} className="flex-1 min-w-0">
                  <Link
                    to={t.to}
                    className={cn(
                      "flex flex-col items-center gap-0.5 px-2 py-3 text-xs font-medium transition-colors whitespace-nowrap min-h-[48px] justify-center",
                      active
                        ? "text-primary"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    <Icon
                      className={cn("h-5 w-5", active && "drop-shadow")}
                      strokeWidth={active ? 2.5 : 2}
                    />
                    <span className="leading-tight">{t.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      )}
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const router = useRouter();

  const handleLogout = async () => {
    await authStore.logout();
    router.invalidate();
  };

  const isAdmin = hasRole(user, "admin");

  return (
    <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-background/80 px-4 py-3 backdrop-blur">
      <div className="min-w-0 flex-1">
        <h1 className="font-display text-lg font-semibold leading-tight truncate">
          {title}
        </h1>
        {subtitle && (
          <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {right}
        {user && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex cursor-pointer items-center gap-1.5 rounded-full bg-card/90 px-3 py-1.5 text-xs font-medium shadow-sm border border-border backdrop-blur hover:bg-accent transition-colors min-h-[36px]">
                <User className="h-3.5 w-3.5 text-primary" />
                <span className="capitalize max-w-20 truncate hidden xs:inline">
                  {user.displayName}
                </span>
                <span className="text-[10px] uppercase text-muted-foreground hidden xs:inline">
                  {user.role}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              <DropdownMenuLabel className="capitalize text-sm">
                {user.displayName}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer py-2.5"
                onClick={() => navigate({ to: "/" })}
              >
                <UserCircle className="h-4 w-4" />
                Profile
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem
                  className="cursor-pointer py-2.5"
                  onClick={() => navigate({ to: "/users" })}
                >
                  <Users className="h-4 w-4" />
                  Manage Users
                </DropdownMenuItem>
              )}
              {hasRole(user, "cashier") && (
                <DropdownMenuItem
                  className="cursor-pointer py-2.5"
                  onClick={() => navigate({ to: "/receipts" })}
                >
                  <FileText className="h-4 w-4" />
                  Receipts
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="cursor-pointer py-2.5"
                onClick={() => {}}
              >
                <Settings className="h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="cursor-pointer py-2.5 text-destructive focus:text-destructive"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}

export const fmt = (n: number) =>
  new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 0,
  }).format(n);
